/* eslint-disable @typescript-eslint/no-unused-vars */
import type { PaginationInfo } from "@core/shared/types";
import { BaseLink } from "@design-system/components/link";
import { useIsRTL } from "@framework/shared/hooks";
import { useLocation } from "@remix-run/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";
import { cn } from "../../utils";

type PaginationSectionProps = {
  paginationInfo?: Pick<PaginationInfo, "page" | "pages">;
};

export default function PaginationSection({
  paginationInfo,
}: PaginationSectionProps) {
  const location = useLocation();
  const isRTL = useIsRTL();

  if (!paginationInfo) return null;

  const totalPages = paginationInfo.pages;
  if (totalPages < 2) return null;

  const page = paginationInfo.page;

  const pageRoute = (page: number) => {
    const currentRoute = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", page.toString());

    return `${currentRoute}?${searchParams.toString()}`;
  };

  const renderPageButtons = (): JSX.Element[] => {
    const buttons: JSX.Element[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      buttons.push(
        <BaseLink
          key={1}
          to={pageRoute(1)}
          className="mx-1 rounded-lg bg-gray-100 px-2 py-1 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:rounded-xl sm:px-3"
        >
          1
        </BaseLink>,
      );
      if (startPage > 2) {
        buttons.push(
          <span
            key="ellipsis1"
            className="mx-1 px-2 text-sm text-gray-500 dark:text-gray-400"
          >
            ...
          </span>,
        );
      }
    }

    // Add visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <BaseLink
          key={i}
          to={pageRoute(i)}
          className={`mx-1 rounded-lg px-2 py-1 text-sm font-medium transition-colors sm:rounded-xl sm:px-3 ${
            i === page
              ? "bg-secondary-800 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          }`}
        >
          {i}
        </BaseLink>,
      );
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span
            key="ellipsis2"
            className="mx-1 px-2 text-sm text-gray-500 dark:text-gray-400"
          >
            ...
          </span>,
        );
      }
      buttons.push(
        <BaseLink
          key={totalPages}
          to={pageRoute(totalPages)}
          className="mx-1 rounded-lg bg-gray-100 px-2 py-1 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:rounded-xl sm:px-3"
        >
          {totalPages}
        </BaseLink>,
      );
    }

    return buttons;
  };

  return (
    <div className="mt-6 flex items-center justify-center space-x-1 sm:mt-8 sm:space-x-2">
      {/* First Page */}
      {page > 1 && (
        <BaseLink
          to={pageRoute(1)}
          className="rounded-lg bg-gray-100 px-2 py-1 text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:rounded-xl sm:px-3"
          aria-label="Go to first page"
        >
          <ChevronsLeftIcon
            className={cn("h-3 w-3", isRTL && "rotate-180", "sm:h-4 sm:w-4")}
          />
        </BaseLink>
      )}

      {/* Previous Page */}
      {page > 1 ? (
        <BaseLink
          to={pageRoute(page - 1)}
          className="rounded-lg bg-gray-100 px-2 py-1 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:rounded-xl sm:px-3"
          aria-label="Go to previous page"
        >
          <ChevronLeftIcon
            className={cn("h-3 w-3", isRTL && "rotate-180", "sm:h-4 sm:w-4")}
          />
        </BaseLink>
      ) : (
        <span className="cursor-not-allowed rounded-lg bg-gray-100 px-2 py-1 text-gray-400 dark:bg-gray-800 dark:text-gray-500 sm:rounded-xl sm:px-3">
          <ChevronLeftIcon
            className={cn("h-3 w-3", isRTL && "rotate-180", "sm:h-4 sm:w-4")}
          />
        </span>
      )}

      {/* Page Numbers */}
      {renderPageButtons()}

      {/* Next Page */}
      {page < totalPages ? (
        <BaseLink
          to={pageRoute(page + 1)}
          className="rounded-lg bg-gray-100 px-2 py-1 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:rounded-xl sm:px-3"
          aria-label="Go to next page"
        >
          <ChevronRightIcon
            className={cn("h-3 w-3", isRTL && "rotate-180", "sm:h-4 sm:w-4")}
          />
        </BaseLink>
      ) : (
        <span className="cursor-not-allowed rounded-lg bg-gray-100 px-2 py-1 text-gray-400 dark:bg-gray-800 dark:text-gray-500 sm:rounded-xl sm:px-3">
          <ChevronRightIcon
            className={cn("h-3 w-3", isRTL && "rotate-180", "sm:h-4 sm:w-4")}
          />
        </span>
      )}

      {/* Last Page */}
      {page < totalPages && (
        <BaseLink
          to={pageRoute(totalPages)}
          className="rounded-lg bg-gray-100 px-2 py-1 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:rounded-xl sm:px-3"
          aria-label="Go to last page"
        >
          <ChevronsRightIcon
            className={cn("h-3 w-3", isRTL && "rotate-180", "sm:h-4 sm:w-4")}
          />
        </BaseLink>
      )}
    </div>
  );
}
